<!--
  Аудиовложение в сообщении чата с встроенным плеером.
-->
<template>
  <div
    class="chat-audio-attachment"
    :class="{
      'chat-audio-attachment_error': audioFailed,
      'chat-audio-attachment_incoming': variant === 'incoming',
    }"
    @click.stop
  >
    <audio
      ref="audio"
      :src="attachmentLink"
      preload="metadata"
      @play="handlePlay"
      @pause="handlePause"
      @loadedmetadata="updateDuration"
      @timeupdate="updateCurrentTime"
      @ended="handleEnded"
      @error="handleError"
    />
    <button
      class="chat-audio-attachment__toggle"
      type="button"
      :title="toggleTitle"
      :aria-label="toggleTitle"
      :disabled="!attachmentLink || audioFailed"
      @click.stop="togglePlayback"
    >
      <AppIcon :name="toggleIcon" :size="15" />
    </button>
    <button
      class="chat-audio-attachment__waveform"
      type="button"
      aria-label="Перемотать аудио"
      :disabled="!duration || audioFailed"
      @click.stop="seekAudio"
    >
      <span
        v-for="(bar, index) in waveformBars"
        :key="index"
        class="chat-audio-attachment__waveform-bar"
        :class="{
          'chat-audio-attachment__waveform-bar_played': index < playedBarCount,
        }"
        :style="{ height: `${bar}%` }"
      />
    </button>
    <span class="chat-audio-attachment__time">
      {{ timeText }}
    </span>
    <a
      v-if="audioFailed && attachmentLink"
      class="chat-audio-attachment__download"
      :href="attachmentLink"
      download
      @click.stop
    >
      Скачать
    </a>
  </div>
</template>

<script>
import AppIcon from "@/components/Icon/AppIcon.vue";
import {
  emptyWaveformBars,
  getAudioWaveformBars,
} from "@/utils/audioWaveformUtils";
import { attachmentUrl, resolveUrl } from "@/utils/fileUtils";

export default {
  name: "ChatAudioAttachment",

  components: {
    AppIcon,
  },

  props: {
    attachment: {
      type: Object,
      required: true,
    },
    variant: {
      type: String,
      default: "outgoing",
      validator(value) {
        return ["incoming", "outgoing"].includes(value);
      },
    },
  },

  data() {
    return {
      audioFailed: false,
      currentTime: 0,
      duration: 0,
      playing: false,
      observer: null,
      waveformBars: emptyWaveformBars(),
      waveformLoading: false,
      waveformRequestId: 0,
    };
  },

  computed: {
    attachmentLink() {
      return resolveUrl(attachmentUrl(this.attachment));
    },

    playedBarCount() {
      if (!this.duration) return 0;

      return Math.round((this.currentTime / this.duration) * this.waveformBars.length);
    },

    timeText() {
      if (this.audioFailed) return "Ошибка";

      const value = this.duration
        ? this.duration - this.currentTime
        : this.currentTime;

      return this.formatDuration(Math.max(value, 0));
    },

    toggleIcon() {
      return this.playing ? "pause" : "play";
    },

    toggleTitle() {
      return this.playing ? "Пауза" : "Воспроизвести";
    },
  },

  mounted() {
    this.observeForWaveform();
  },

  beforeUnmount() {
    this.pauseAudio();
    this.disconnectObserver();
    this.waveformRequestId += 1;
  },

  watch: {
    attachmentLink() {
      this.audioFailed = false;
      this.currentTime = 0;
      this.duration = 0;
      this.playing = false;
      this.waveformBars = emptyWaveformBars();
      this.waveformRequestId += 1;
      this.observeForWaveform();
    },
  },

  methods: {
    observeForWaveform() {
      this.disconnectObserver();
      if (!this.attachmentLink) return;

      if (!window.IntersectionObserver) {
        this.loadWaveform();
        return;
      }

      // В истории может быть много голосовых. Пики строим только рядом
      // с viewport, иначе мобильный браузер скачает и декодирует все аудио
      this.observer = new IntersectionObserver(
        (entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;

          this.disconnectObserver();
          this.loadWaveform();
        },
        { rootMargin: "160px" }
      );
      this.observer.observe(this.$el);
    },

    disconnectObserver() {
      this.observer?.disconnect();
      this.observer = null;
    },

    async loadWaveform() {
      if (!this.attachmentLink) return;

      const requestId = this.waveformRequestId + 1;
      this.waveformRequestId = requestId;

      this.waveformLoading = true;

      try {
        const bars = await getAudioWaveformBars(this.attachmentLink);
        if (this.waveformRequestId !== requestId) return;

        this.waveformBars = bars;
      } catch {
        this.waveformBars = emptyWaveformBars();
      } finally {
        if (this.waveformRequestId === requestId) {
          this.waveformLoading = false;
        }
      }
    },

    async togglePlayback() {
      if (this.playing) {
        this.pauseAudio();
        return;
      }

      await this.playAudio();
    },

    async playAudio() {
      try {
        await this.$refs.audio.play();
        this.playing = true;
      } catch {
        this.audioFailed = true;
        this.playing = false;
      }
    },

    pauseAudio() {
      this.$refs.audio?.pause();
      this.playing = false;
    },

    seekAudio(event) {
      if (!this.duration) return;

      const rect = event.currentTarget.getBoundingClientRect();
      const position = (event.clientX - rect.left) / rect.width;
      this.$refs.audio.currentTime =
        this.duration * Math.min(Math.max(position, 0), 1);
    },

    updateDuration() {
      this.duration = this.$refs.audio.duration || 0;
    },

    updateCurrentTime() {
      this.currentTime = this.$refs.audio.currentTime || 0;
    },

    handlePlay() {
      this.playing = true;
    },

    handlePause() {
      this.playing = false;
    },

    handleEnded() {
      this.playing = false;
      this.currentTime = 0;
    },

    handleError() {
      this.audioFailed = true;
      this.playing = false;
    },

    formatDuration(value) {
      const minutes = Math.floor(value / 60);
      const seconds = Math.floor(value % 60).toString().padStart(2, "0");

      return `${minutes}:${seconds}`;
    },

  },
};
</script>

<style scoped>
.chat-audio-attachment {
  align-items: center;
  display: grid;
  grid-template-columns: 28px minmax(76px, 1fr) 34px;
  gap: 8px;
  width: min(236px, 100%);
  max-width: 100%;
  min-height: 34px;
}

.chat-audio-attachment audio {
  display: none;
}

.chat-audio-attachment__toggle {
  align-items: center;
  background: rgba(255, 255, 255, 0.72);
  border: 0;
  border-radius: 50%;
  color: #1c5947;
  cursor: pointer;
  display: inline-flex;
  height: 28px;
  justify-content: center;
  padding: 0;
  width: 28px;
}

.chat-audio-attachment__toggle:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.chat-audio-attachment__waveform {
  align-items: center;
  background: transparent;
  border: 0;
  cursor: pointer;
  display: grid;
  gap: 2px;
  grid-template-columns: repeat(28, 1fr);
  height: 24px;
  min-width: 0;
  padding: 0;
  width: 100%;
}

.chat-audio-attachment__waveform:disabled {
  cursor: default;
  opacity: 0.65;
}

.chat-audio-attachment__waveform-bar {
  align-self: center;
  background: rgba(0, 0, 0, 0.18);
  border-radius: 2px;
  display: block;
  min-height: 4px;
  transition: background-color 0.12s linear;
}

.chat-audio-attachment__waveform-bar_played {
  background: #1f8f70;
}

.chat-audio-attachment__time {
  color: #555;
  font-size: 10px;
  line-height: 1;
  text-align: right;
  white-space: nowrap;
}

.chat-audio-attachment__download {
  color: #1f6feb;
  font-size: 11px;
  grid-column: 2 / 4;
  line-height: 1.1;
  text-decoration: none;
}

.chat-audio-attachment_error {
  grid-template-columns: 28px minmax(76px, 1fr) 42px;
}

.chat-audio-attachment_incoming .chat-audio-attachment__toggle {
  color: #285a90;
}

.chat-audio-attachment_incoming .chat-audio-attachment__waveform-bar_played {
  background: #3178c6;
}

@media (max-width: 768px) {
  .chat-audio-attachment {
    width: min(214px, 100%);
  }
}
</style>
